import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory } from 'react-router';
import { toast } from 'react-toastify';

import { DropDownOptions } from 'devextreme-react/autocomplete';
import Form, { Item, RequiredRule } from 'devextreme-react/form';
import ValidationGroup from 'devextreme-react/validation-group';
import Validator from 'devextreme-react/validator';

import { AppCol, AppFieldBox, AppLayoutTop } from 'components/appComponents';
import { MONEY_FORMAT } from 'constants/formats';
import { Button, DateBox, NumberBox, SelectBox, TextArea } from 'devextreme-react';
import $createDataSource from 'utils/create-data-souce-instance';

import {
  useChangeCashFlowIncomeOwnerMutation,
  useCreateCashFlowIncomeOwnerMutation,
  useLazyGetCashFlowIncomeOwnerQuery,
} from 'bus/cash';

import { initValue } from './utils';

type Props = {
  id: string;
  cashFlowId: string | number;
};

const CashFlowIncomeOwner: FC<Props> = ({ id, cashFlowId }) => {
  const history = useHistory();
  const [form, setForm] = useState(initValue);
  const createValidationRef = useRef<ValidationGroup>(null);

  const [changeCashFlowIncomeOwner, { isLoading: isLoadingChange }] = useChangeCashFlowIncomeOwnerMutation();
  const [createCashFlowIncomeOwner, { isLoading: isLoadingCreate }] = useCreateCashFlowIncomeOwnerMutation();
  const [getCashFlowIncomeOwnerQuery] = useLazyGetCashFlowIncomeOwnerQuery();

  const disabledSubmit = isLoadingChange || isLoadingCreate;

  const handleSubmit = async (e: any) => {
    e.preventDefault && e.preventDefault();

    if (createValidationRef.current?.instance.validate().isValid) {
      if (id) {
        await changeCashFlowIncomeOwner({ id, ...form });
        toast.success('Изменения успешно сохранены', {
          position: toast.POSITION.BOTTOM_RIGHT,
        });
      } else {
        await createCashFlowIncomeOwner(form);
        history.push('/cash-flow');
      }
    }
  };

  const requestDateSource = useMemo(() => {
    const defaultFilter = ['cashFlowType', '=', '0'];
    return $createDataSource('/v1/CashFlowItem', 'id', { defaultFilter });
  }, []);

  const getInit = useCallback(async () => {
    if (id) {
      const result = await getCashFlowIncomeOwnerQuery({ id });
      result && setForm(result.data!);
    }
  }, [id, getCashFlowIncomeOwnerQuery]);

  useEffect(() => {
    getInit();
  }, [getInit]);

  return (
    <div className="mt-5" style={{ width: 750 }}>
      <form onSubmit={handleSubmit}>
        <ValidationGroup ref={createValidationRef}>
          <AppLayoutTop title="Приход от владельца">
            <Button
              text="Сохранить"
              type="default"
              useSubmitBehavior={false}
              onClick={handleSubmit}
              disabled={disabledSubmit}
            />
          </AppLayoutTop>
          <div className="dx-card">
            <Form formData={form}>
              <Item colSpan={12} colCount={12} itemType="group">
                <AppCol col={4}>
                  <AppFieldBox label="Дата" labelTop>
                    <DateBox
                      stylingMode="underlined"
                      type="date"
                      onValueChange={(e) => setForm((prev) => ({ ...prev, date: e as string }))}
                      useMaskBehavior
                      dateSerializationFormat="yyyy-MM-dd"
                      value={form.date}
                    >
                      <Validator>
                        <RequiredRule message="Заполните поле Дата" />
                      </Validator>
                    </DateBox>
                  </AppFieldBox>
                </AppCol>
                <AppCol col={4}>
                  <AppFieldBox label="Статья движения ДС" labelTop>
                    <SelectBox
                      stylingMode="underlined"
                      dataSource={requestDateSource}
                      displayExpr="name"
                      valueExpr="id"
                      value={form.cashFlowItemId}
                      searchEnabled
                      onItemClick={(e) => {
                        setForm((prev) => ({ ...prev, cashFlowItemId: e.itemData.id }));
                      }}
                    >
                      <DropDownOptions
                        title="Выберите Статья движения ДС"
                        closeOnOutsideClick={true}
                        showTitle={false}
                      />
                      <Validator>
                        <RequiredRule message="Выберите Статья движения ДС" />
                      </Validator>
                    </SelectBox>
                  </AppFieldBox>
                </AppCol>

                <AppCol col={4}>
                  <AppFieldBox label="Сумма" labelTop>
                    <NumberBox
                      stylingMode="underlined"
                      placeholder="0"
                      format={MONEY_FORMAT}
                      value={form.sum}
                      onValueChanged={(e) => {
                        setForm((prev) => ({ ...prev, sum: e.value }));
                      }}
                    >
                      <Validator>
                        <RequiredRule message="Заполните поле Сумма" />
                      </Validator>
                    </NumberBox>
                  </AppFieldBox>
                </AppCol>

                <AppCol col={12}>
                  <AppFieldBox label="Комментарий" labelTop>
                    <TextArea
                      stylingMode="underlined"
                      placeholder="Введите комментарий"
                      mode="text"
                      value={form.comment || ''}
                      onChange={(e: any) => {
                        setForm((prev) => ({ ...prev, comment: e.event.currentTarget.value }));
                      }}
                    />
                  </AppFieldBox>
                </AppCol>
              </Item>
            </Form>
          </div>
        </ValidationGroup>
      </form>
    </div>
  );
};

export default CashFlowIncomeOwner;
